import { BroadcastEventType, type DatasetImportPhase } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';

/**
 * Channel for the half of an import only the uploading tab can see.
 *
 * The bytes travel over HTTP, so `upload.onprogress` in this browser is the
 * only thing that knows how far they have got — the server has nothing to
 * broadcast until they land. Kept off the server's own event name so the shared
 * payload contract does not grow a field only a client ever sets, and read
 * through the same bus so one watchable shape covers both.
 */
export const LOCAL_DATASET_IMPORT_EVENT = 'dataset-import-local';

export const DATASET_IMPORT_EVENT = BroadcastEventType.DatasetImport;

/**
 * What to show about an in-flight import.
 *
 * `uploading` is this tab's own XHR progress; the rest come from the saga. One
 * union because the user is watching one operation, and the boundary between
 * "still sending" and "server is reading" is not a distinction worth making
 * them track.
 */
export type DatasetImportView =
  | { phase: 'uploading'; fraction: number }
  | { phase: 'staged' | 'parsing' | 'indexing' }
  | { phase: 'failed'; error: string | null };

/** `ready` is not a view — it is the absence of one — so it is modelled apart. */
export type DatasetImportSignal = DatasetImportView | { phase: 'ready' };

/**
 * A signal with the order it arrived in.
 *
 * The phases reach the page on two independent channels, and neither is
 * authoritative: this tab's upload runs before the saga exists, the saga's
 * phases come after. Merging them needs "most recent", and a watchable only
 * holds its own last value — so recency has to be carried, not inferred.
 *
 * Ordering by phase instead would look tidier and be wrong: a second upload to
 * the same dataset starts back at `uploading`, which is *behind* the `ready`
 * still sitting in the other channel, and would never show.
 */
export type StampedDatasetImportSignal = {
  seq: number;
  signal: DatasetImportSignal;
};

let sequence = 0;

/** Monotonic across both channels, which is the whole point of it. */
export const nextDatasetImportSequence = (): number => {
  sequence += 1;
  return sequence;
};

/** Whether a payload concerns this dataset at all. */
export const isDatasetImportPayloadFor = (
  payload: unknown,
  datasetId: string
): boolean =>
  typeof payload === 'object' &&
  payload !== null &&
  (payload as { datasetId?: unknown }).datasetId === datasetId;

type ImportBroadcast = {
  datasetId?: unknown;
  phase?: unknown;
  error?: unknown;
  fraction?: unknown;
};

const SERVER_PHASES: ReadonlySet<string> = new Set<DatasetImportPhase>([
  'staged',
  'parsing',
  'indexing',
  'ready',
  'failed',
]);

/**
 * Uploads still sending, by dataset.
 *
 * An upload starts before the page that shows it: a CSV dataset is created so
 * the browser has somewhere to send the file, and the page opens on it while
 * the bytes are still going. A page mounting into that has missed every event
 * so far, and the persisted status — `importing`, meaning the server is reading
 * — is not what is happening yet. This is what it reads instead.
 *
 * Only `uploading` is held: every later phase is the server's, and the page
 * falls back to the persisted status for those, which is authoritative and
 * survives a reload.
 */
const uploadsInFlight = new Map<string, DatasetImportView>();

/** The upload this browser is still sending for a dataset, if there is one. */
export const inFlightDatasetUpload = (
  datasetId: string
): DatasetImportView | null => uploadsInFlight.get(datasetId) ?? null;

/** Publishes this tab's upload progress so the dataset page can render it. */
export const emitLocalDatasetImport = (
  datasetId: string,
  signal: DatasetImportSignal
): void => {
  if (signal.phase === 'uploading') {
    uploadsInFlight.set(datasetId, signal);
  } else {
    uploadsInFlight.delete(datasetId);
  }
  emit(LOCAL_DATASET_IMPORT_EVENT, { datasetId, ...signal });
};

/**
 * Reads a broadcast into a view, or null when it is not about this dataset or
 * not a phase this build understands — a payload from a newer server must not
 * strand the page on a stale phase.
 */
export const datasetImportSignalFromPayload = (
  payload: unknown,
  datasetId: string
): DatasetImportSignal | null => {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }
  const event = payload as ImportBroadcast;
  if (event.datasetId !== datasetId || typeof event.phase !== 'string') {
    return null;
  }
  if (event.phase === 'uploading') {
    return {
      phase: 'uploading',
      fraction: typeof event.fraction === 'number' ? event.fraction : 0,
    };
  }
  if (!SERVER_PHASES.has(event.phase)) {
    return null;
  }
  if (event.phase === 'ready') {
    return { phase: 'ready' };
  }
  if (event.phase === 'failed') {
    return {
      phase: 'failed',
      error: typeof event.error === 'string' ? event.error : null,
    };
  }
  return { phase: event.phase as 'staged' | 'parsing' | 'indexing' };
};

/**
 * The view a freshly loaded page starts from.
 *
 * The persisted status is all a page reloaded mid-import has: the events it was
 * not around for are gone. `importing` resolves to `parsing` because that is
 * the long phase and so overwhelmingly the one it is in; the next event
 * corrects it.
 */
export const datasetImportViewFromStatus = (
  status: 'importing' | 'ready' | 'failed' | undefined,
  error: string | null | undefined
): DatasetImportView | null => {
  if (status === 'failed') {
    return { phase: 'failed', error: error ?? null };
  }
  if (status === 'importing') {
    return { phase: 'parsing' };
  }
  return null;
};
